import fetch from '../lib/fetch';

interface StreamRequestEvents {
  headersReceived: Event;
  progress: Event;
  load: Event;
  error: Event;
  abort: Event;
}

export type StreamRequestBody = Record<
  string,
  string | number | boolean | null
>;

/**
 * `StreamRequest` provides a modern interface for handling streaming requests using the Fetch API.
 */
export default class StreamRequest {
  private src: string | null = null;

  private _response: Response | null = null;

  private _status: number = 0;

  private headers: Headers;

  private controller: AbortController;

  private eventTarget: EventTarget;

  private chunks: Uint8Array[] = [];

  private onChunkCallback?: (chunk: Uint8Array) => void;

  constructor() {
    this.headers = new Headers();
    this.controller = new AbortController();
    this.eventTarget = new EventTarget();
  }

  /**
   * Set a callback to receive chunks as they arrive during streaming.
   * This enables progressive processing of the stream data.
   */
  public setOnChunk(callback: (chunk: Uint8Array) => void): void {
    this.onChunkCallback = callback;
  }

  public get response(): Response | null {
    return this._response;
  }

  public get status(): number {
    return this._status;
  }

  public getResponseHeader(name: string): string | null {
    return this.headers.get(name);
  }

  public abort(): void {
    this.controller.abort();
  }

  public addEventListener<K extends keyof StreamRequestEvents>(
    type: K,
    listener: (event: StreamRequestEvents[K]) => void
  ): void {
    this.eventTarget.addEventListener(type, listener as EventListener);
  }

  public removeEventListener<K extends keyof StreamRequestEvents>(
    type: K,
    listener: (event: StreamRequestEvents[K]) => void
  ): void {
    this.eventTarget.removeEventListener(type, listener as EventListener);
  }

  /**
   * Start the stream call.
   *
   * @param url - The URL to send the request to
   * @param body - The request body to send
   * @returns The StreamRequest instance for chaining
   */
  public async post(url: string, body: StreamRequestBody): Promise<this> {
    const timeoutMs = 60000;
    const startTime = Date.now();
    let headersReceived = false;
    let timedOut = false;

    const timeoutId = setTimeout(() => {
      timedOut = true;
      this.controller.abort();
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(body),
        signal: this.controller.signal,
      });

      clearTimeout(timeoutId);

      this._response = response;
      this._status = response.status;
      this.headers = response.headers;
      headersReceived = true;

      this.eventTarget.dispatchEvent(new Event('headersReceived'));

      if (!response.ok) {
        // For non-200 responses, try to read the error message before consuming the stream
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorMessage;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the default message
        }

        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('Response body is not readable');
      }

      const reader = response.body.getReader();

      const processStream = async (): Promise<void> => {
        const { done, value } = await reader.read();

        if (done) {
          this.eventTarget.dispatchEvent(new Event('load'));
          return;
        }

        if (this.onChunkCallback) {
          this.onChunkCallback(value);
        }

        this.chunks.push(value);
        this.eventTarget.dispatchEvent(new Event('progress'));

        return processStream();
      };

      await processStream();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        const elapsedMs = Date.now() - startTime;

        if (error.name === 'AbortError') {
          let errorPhase: string;
          if (!timedOut) {
            errorPhase = 'user_aborted';
          } else if (headersReceived) {
            errorPhase = 'timeout_during_stream';
          } else {
            errorPhase = 'timeout_before_headers';
          }

          const debugContext = {
            errorPhase,
            elapsedMs,
            headersReceived,
            chunksReceived: this.chunks.length,
            httpStatus: this._status,
          };

          this.eventTarget.dispatchEvent(
            new CustomEvent('abort', { detail: debugContext })
          );
        } else {
          let errorMessage = error.message;
          if (this.chunks.length > 0) {
            try {
              const blob = new Blob(this.chunks as BlobPart[]);
              const text = await blob.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
            } catch (parseError) {
              // If we can't parse the chunks, use the original error message
            }
          }

          let errorPhase: string;
          if (!headersReceived) {
            errorPhase = 'request_failed';
          } else if (this.chunks.length > 0) {
            errorPhase = 'stream_read_error';
          } else {
            errorPhase = 'post_headers_error';
          }

          this.eventTarget.dispatchEvent(
            new CustomEvent('error', {
              detail: {
                message: errorMessage,
                debugContext: {
                  errorPhase,
                  elapsedMs,
                  headersReceived,
                  chunksReceived: this.chunks.length,
                  httpStatus: this._status,
                  originalError: error.message,
                },
              },
            })
          );
        }
      }
    }

    return this;
  }

  /**
   * Get the object URL for the response blob.
   *
   * @returns A promise that resolves to the object URL or undefined if not available
   */
  public getObjectURL(): string | undefined {
    if (this.src) {
      return this.src;
    }

    if (this._response && this._status === 200) {
      const blob = new Blob(this.chunks as BlobPart[]);
      this.src = window.URL.createObjectURL(blob);
      return this.src;
    }

    return undefined;
  }
}
